<div class="tcal">

  <!-- ─── En-tête ──────────────────────────────────────────────────── -->
  <header class="tcal__head">
    <div>
      <h1 class="tcal__title">
        <i class="fa-solid fa-calendar-days"></i>
        {{ 'CALENDAR.TITLE' | translate }}
      </h1>
      <p class="tcal__sub">{{ 'CALENDAR.TEACHER_SUBTITLE' | translate }}</p>
    </div>

    <!-- Toujours actif : la création ne dépend d'aucun filtre. -->
    <button class="tcal__cta" type="button" (click)="openCreate()">
      <i class="fa-solid fa-plus"></i>
      {{ 'CALENDAR.CREATE_BTN' | translate }}
    </button>
  </header>

  <!-- ─── Statistiques de la période affichée ──────────────────────── -->
  <div class="tcal__stats">
    <div class="tcal-stat">
      <span class="tcal-stat__value">{{ stats().count }}</span>
      <span class="tcal-stat__label">{{ 'CALENDAR.STATS.SESSIONS' | translate }}</span>
    </div>
    <div class="tcal-stat">
      <span class="tcal-stat__value">{{ stats().hours }}</span>
      <span class="tcal-stat__label">{{ 'CALENDAR.STATS.HOURS' | translate }}</span>
    </div>
    <div class="tcal-stat">
      <span class="tcal-stat__value">{{ stats().subjects }}</span>
      <span class="tcal-stat__label">{{ 'CALENDAR.STATS.SUBJECTS' | translate }}</span>
    </div>
    <div class="tcal-stat">
      <span class="tcal-stat__value">{{ stats().groups }}</span>
      <span class="tcal-stat__label">{{ 'CALENDAR.STATS.GROUPS' | translate }}</span>
    </div>
  </div>

  <!-- ─── Filtres ──────────────────────────────────────────────────── -->
  <div class="tcal__filters">
    <label class="tcal-filter">
      <span class="tcal-filter__label">{{ 'CALENDAR.FILTER.SUBJECT' | translate }}</span>
      <!-- Groupé par parcours, comme le formulaire. Le suffixe ne s'applique
           qu'aux homonymes : sans en-tête de groupe, on ne saurait pas de quel
           niveau relève une matière qui n'en a pas. -->
      <select [(ngModel)]="filterSubjectId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'CALENDAR.FILTER.ALL_SUBJECTS' | translate }}</option>
        @for (g of subjectGroups(); track g.pathId) {
          <optgroup [label]="g.pathLabel || ('CALENDAR.CREATE.NO_PATH' | translate)">
            @for (s of g.subjects; track s.id) {
              <option [ngValue]="s.id">{{ subjectLabel(s) }}</option>
            }
          </optgroup>
        }
      </select>
    </label>

    <label class="tcal-filter">
      <span class="tcal-filter__label">{{ 'CALENDAR.FILTER.GROUP' | translate }}</span>
      <select [(ngModel)]="filterGroupId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'CALENDAR.FILTER.ALL_GROUPS' | translate }}</option>
        @for (g of options().groups; track g.id) {
          <option [ngValue]="g.id">{{ g.name }}</option>
        }
      </select>
    </label>

    <label class="tcal-filter">
      <span class="tcal-filter__label">{{ 'CALENDAR.FILTER.STUDENT' | translate }}</span>
      <select [(ngModel)]="filterStudentId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'CALENDAR.FILTER.ALL_STUDENTS' | translate }}</option>
        @for (s of options().students; track s.id) {
          <option [ngValue]="s.id">{{ s.fullName }}</option>
        }
      </select>
    </label>

    @if (hasFilters) {
      <button class="tcal-filter__clear" type="button" (click)="clearFilters()">
        <i class="fa-solid fa-xmark"></i>
        {{ 'CALENDAR.FILTER.CLEAR' | translate }}
      </button>
    }
  </div>

  <!-- ─── Corps : calendrier + panneau latéral ─────────────────────── -->
  <div class="tcal__body">

    <section class="tcal-card">
      <!-- Barre de navigation maison : plus lisible que celle de la librairie -->
      <div class="tcal-toolbar">
        <div class="tcal-toolbar__nav">
          <button type="button" class="tcal-nav-btn" (click)="prev()"
                  [attr.aria-label]="'CALENDAR.NAV.PREV' | translate">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <button type="button" class="tcal-nav-btn" (click)="next()"
                  [attr.aria-label]="'CALENDAR.NAV.NEXT' | translate">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          <button type="button" class="tcal-today" (click)="today()">
            {{ 'CALENDAR.NAV.TODAY' | translate }}
          </button>
        </div>

        <h2 class="tcal-toolbar__period">{{ periodLabel() }}</h2>

        <div class="tcal-seg" role="tablist">
          <button type="button" role="tab" class="tcal-seg__btn"
                  [class.is-active]="activeView() === 'dayGridMonth'"
                  (click)="switchView('dayGridMonth')">
            {{ 'CALENDAR.NAV.MONTH' | translate }}
          </button>
          <button type="button" role="tab" class="tcal-seg__btn"
                  [class.is-active]="activeView() === 'timeGridWeek'"
                  (click)="switchView('timeGridWeek')">
            {{ 'CALENDAR.NAV.WEEK' | translate }}
          </button>
          <button type="button" role="tab" class="tcal-seg__btn"
                  [class.is-active]="activeView() === 'timeGridDay'"
                  (click)="switchView('timeGridDay')">
            {{ 'CALENDAR.NAV.DAY' | translate }}
          </button>
        </div>
      </div>

      @if (errored()) {
        <!-- Erreur de chargement : jamais de séances de substitution -->
        <app-error-state titleKey="CALENDAR.ERROR_TITLE"
                         messageKey="CALENDAR.ERROR_DESC"
                         (retry)="reload()"></app-error-state>
      }

      <div class="tcal-grid" [class.is-loading]="loading()" [class.is-hidden]="errored()">
        <full-calendar [options]="calendarOptions"></full-calendar>
        @if (loading()) {
          <div class="tcal-grid__veil"><span class="tcal-spinner"></span></div>
        }
      </div>
    </section>

    <!-- Panneau « à venir » : la lecture rapide que la grille ne donne pas -->
    <aside class="tcal-side">
      <h3 class="tcal-side__title">
        <i class="fa-solid fa-arrow-trend-up"></i>
        {{ 'CALENDAR.UPCOMING' | translate }}
      </h3>

      @if (upcoming().length === 0) {
        <p class="tcal-side__empty">{{ 'CALENDAR.UPCOMING_EMPTY' | translate }}</p>
      } @else {
        <ul class="tcal-side__list">
          @for (s of upcoming(); track s.id) {
            <li>
              <button type="button" class="tcal-up" [class]="view.colorClass(s)"
                      (click)="openDetail(s)">
                <span class="tcal-up__date">
                  <span class="tcal-up__day">{{ start(s) | date:'dd' }}</span>
                  <span class="tcal-up__month">{{ start(s) | localizedDate:'MMM' }}</span>
                </span>
                <span class="tcal-up__body">
                  <span class="tcal-up__title">{{ s.title }}</span>
                  <span class="tcal-up__meta">
                    {{ time(s.startTime) }}–{{ time(s.endTime) }}
                    @if (view.subjectLabel(s)) { · {{ view.subjectLabel(s) }} }
                  </span>
                </span>
              </button>
            </li>
          }
        </ul>
      }
    </aside>
  </div>

  <!-- ─── Formulaire de séance (création / modification) ─────────────────
       `<dialog>` + showModal() : le navigateur peint le panneau dans la couche
       supérieure, hors de tout contexte d'empilement de la coquille. C'est ce
       qui l'empêche de repasser sous le calendrier. -->
  <dialog #formDialog class="tcal-dialog tcal-modal"
          (close)="onFormClosed()"
          (cancel)="onFormCancel($event)"
          (mousedown)="onPressStart($event)"
          (click)="onBackdropClick($event, formDialog)">
    @if (showForm()) {
      <div class="tcal-modal__panel" [class]="preview().colorClass">

      <!-- ─── Aperçu vivant ────────────────────────────────────────────────
           La séance qu'on est en train de composer, sous la forme exacte
           qu'elle prendra dans la grille. Le professeur voit son créneau se
           former plutôt qu'un intitulé de formulaire. -->
      <header class="tcal-compose">
        <button type="button" class="tcal-modal__close" (click)="closeForm()"
                [attr.aria-label]="'CALENDAR.CREATE.CANCEL' | translate">
          <i class="fa-solid fa-xmark"></i>
        </button>

        <p class="tcal-compose__eyebrow">
          {{ (editingId() === null ? 'CALENDAR.CREATE.TITLE' : 'CALENDAR.EDIT.TITLE') | translate }}
        </p>

        <!-- L'échec d'enregistrement s'affiche ici, en tête et sans défilement.
             Une alerte flottante était peinte dans la couche supérieure : elle
             recouvrait la modale sans jamais recevoir le clic, et il fallait
             fermer la modale — donc perdre la saisie — pour s'en défaire. -->
        @if (formError()) {
          <p class="tcal-compose__alert" role="alert">
            <i class="fa-solid fa-circle-exclamation"></i>
            <span>{{ formError() }}</span>
            <button type="button" class="tcal-compose__alert-close"
                    (click)="formError.set(null)"
                    [attr.aria-label]="'ACTIONS.CLOSE' | translate">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </p>
        }

        <div class="tcal-compose__card">
          <span class="tcal-compose__slot">
            <span class="tcal-compose__time">{{ form.startTime || '--:--' }}</span>
            <span class="tcal-compose__arrow" aria-hidden="true">→</span>
            <span class="tcal-compose__time">{{ endTime || '--:--' }}</span>
          </span>

          <h2 class="tcal-compose__name">
            {{ form.title.trim() || ('CALENDAR.CREATE.UNTITLED' | translate) }}
          </h2>

          <p class="tcal-compose__meta">
            <span class="tcal-compose__kind">
              <i [class]="view.kindIcon(form.kind)"></i>
              {{ view.kindLabelKey(form.kind) | translate }}
            </span>
            @if (preview().subject) {
              <span class="tcal-compose__dot" aria-hidden="true"></span>
              <span>{{ preview().subject }}</span>
            }
            <span class="tcal-compose__dot" aria-hidden="true"></span>
            @if (audienceMode() === 'recording') {
              <span><i class="fa-solid fa-clapperboard"></i> {{ 'CALENDAR.PRODUCTION.BADGE' | translate }}</span>
            } @else if (preview().count) {
              <span>{{ 'CALENDAR.CREATE.ATTENDEE_COUNT' | translate:{ count: preview().count } }}</span>
            } @else {
              <span class="tcal-compose__todo">{{ 'CALENDAR.CREATE.NO_TARGET_YET' | translate }}</span>
            }
          </p>
        </div>
      </header>

      <div class="tcal-modal__body">

        @if (options().subjects.length === 0) {
          <p class="tcal-alert tcal-alert--warn">
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ 'CALENDAR.NO_SUBJECTS' | translate }}
          </p>
        }

        <!-- ─── Ce qu'on enseigne ─────────────────────────────────────── -->
        <section class="tcal-sec tcal-sec--what">
          <h3 class="tcal-sec__title">{{ 'CALENDAR.CREATE.SECTION_WHAT' | translate }}</h3>

          <fieldset class="tcal-group tcal-kinds">
            <legend class="tcal-sr">{{ 'CALENDAR.CREATE.KIND' | translate }}</legend>
            @for (k of kinds; track k) {
              <button type="button" class="tcal-kind"
                      [class.is-active]="form.kind === k"
                      [attr.aria-pressed]="form.kind === k"
                      (click)="form.kind = k">
                <i [class]="view.kindIcon(k)"></i>
                {{ view.kindLabelKey(k) | translate }}
              </button>
            }
          </fieldset>

          <div class="tcal-field" [class.is-invalid]="titleInvalid()">
            <label for="sess-title">{{ 'CALENDAR.CREATE.FORM_TITLE' | translate }} *</label>
            <input id="sess-title" type="text" [(ngModel)]="form.title"
                   [attr.aria-invalid]="titleInvalid() || null"
                   [placeholder]="'CALENDAR.CREATE.FORM_TITLE_PLACEHOLDER' | translate">
            @if (titleInvalid()) {
              <p class="tcal-field__error" role="alert">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ 'CALENDAR.CREATE.TITLE_REQUIRED' | translate }}
              </p>
            }
          </div>

          <!-- Matières : le rattachement structurant — la première cochée donne
               sa couleur à la séance sur toute la plateforme.

               Une séance en porte souvent plusieurs : le même cours d'anglais
               vaut pour la 6ème primaire, la 9ème de base et le baccalauréat,
               qui sont trois matières distinctes au référentiel. D'où des cases
               plutôt qu'un menu, et un regroupement par parcours plutôt qu'un
               filtre — filtrer imposerait un aller-retour par niveau pour
               composer ce qui doit se lire d'un coup. -->
          <fieldset class="tcal-field tcal-subjects" [class.is-invalid]="subjectsInvalid()">
            <legend>{{ 'CALENDAR.CREATE.SUBJECTS' | translate }} *</legend>

            @if (subjectGroups().length === 0) {
              <p class="tcal-note">{{ 'CALENDAR.CREATE.NO_SUBJECT' | translate }}</p>
            }

            @for (g of subjectGroups(); track g.pathId) {
              <div class="tcal-subjects__path">
                <p class="tcal-subjects__title">
                  {{ g.pathLabel || ('CALENDAR.CREATE.NO_PATH' | translate) }}
                </p>
                <div class="tcal-subjects__list">
                  @for (s of g.subjects; track s.id) {
                    <button type="button" class="tcal-chip"
                            [class.is-picked]="isSubjectPicked(s.id)"
                            [attr.aria-pressed]="isSubjectPicked(s.id)"
                            (click)="toggleSubject(s.id)">
                      <i class="fa-solid" [class.fa-square-check]="isSubjectPicked(s.id)"
                         [class.fa-square]="!isSubjectPicked(s.id)"></i>
                      <span>{{ subjectLabel(s) }}</span>
                      @if (isPrincipalSubject(s.id)) {
                        <span class="tcal-chip__flag">{{ 'CALENDAR.CREATE.PRINCIPAL' | translate }}</span>
                      }
                    </button>
                  }
                </div>
              </div>
            }

            @if (form.subjectIds.length > 1) {
              <p class="tcal-note">
                <i class="fa-solid fa-circle-info"></i>
                <span>{{ 'CALENDAR.CREATE.PRINCIPAL_HINT' | translate }}</span>
              </p>
            }

            @if (subjectsInvalid()) {
              <p class="tcal-field__error" role="alert">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ 'CALENDAR.CREATE.SUBJECT_REQUIRED' | translate }}
              </p>
            }
          </fieldset>
        </section>

        <!-- ─── Pour qui ──────────────────────────────────────────────────
             Deux intentions explicites plutôt qu'un champ laissé vide : une
             séance suivie par des élèves, ou un créneau d'enregistrement. -->
        <section class="tcal-sec tcal-sec--who">
          <h3 class="tcal-sec__title">{{ 'CALENDAR.CREATE.SECTION_WHO' | translate }}</h3>

          <fieldset class="tcal-group tcal-switch">
            <legend class="tcal-sr">{{ 'CALENDAR.CREATE.SECTION_WHO' | translate }}</legend>
            <button type="button" class="tcal-switch__btn"
                    [class.is-active]="audienceMode() === 'students'"
                    [attr.aria-pressed]="audienceMode() === 'students'"
                    (click)="setAudience('students')">
              <i class="fa-solid fa-user-group"></i>
              {{ 'CALENDAR.CREATE.MODE_AUDIENCE' | translate }}
            </button>
            <button type="button" class="tcal-switch__btn"
                    [class.is-active]="audienceMode() === 'recording'"
                    [attr.aria-pressed]="audienceMode() === 'recording'"
                    (click)="setAudience('recording')">
              <i class="fa-solid fa-clapperboard"></i>
              {{ 'CALENDAR.CREATE.MODE_RECORDING' | translate }}
            </button>
          </fieldset>

          @if (audienceMode() === 'recording') {
            <p class="tcal-note">
              <i class="fa-solid fa-circle-info"></i>
              <span>{{ 'CALENDAR.CREATE.PRODUCTION_HINT' | translate }}</span>
            </p>
          } @else {
            <div class="tcal-field">
              <label for="sess-group">{{ 'CALENDAR.CREATE.GROUP' | translate }}</label>
              <select id="sess-group" [(ngModel)]="form.groupId" (ngModelChange)="onGroupChange()">
                <option [ngValue]="null">{{ 'CALENDAR.CREATE.NO_GROUP' | translate }}</option>
                @for (g of options().groups; track g.id) {
                  <option [ngValue]="g.id">{{ g.name }} ({{ g.memberCount }})</option>
                }
              </select>
            </div>

            <div class="tcal-field">
              <label for="sess-students">
                {{ 'CALENDAR.CREATE.STUDENTS' | translate }}
                @if (form.studentIds.length) {
                  <span class="tcal-count">{{ form.studentIds.length }}</span>
                }
              </label>
              <input id="sess-students" type="search" class="tcal-search"
                     [ngModel]="studentQuery()"
                     (ngModelChange)="studentQuery.set($event)"
                     [placeholder]="'CALENDAR.CREATE.STUDENT_SEARCH' | translate">

              <div class="tcal-picker">
                @for (s of pickableStudents(); track s.id) {
                  <button type="button" class="tcal-chip"
                          [class.is-picked]="isStudentPicked(s.id)"
                          [attr.aria-pressed]="isStudentPicked(s.id)"
                          (click)="toggleStudent(s.id)">
                    <i class="fa-solid" [class.fa-check]="isStudentPicked(s.id)"
                       [class.fa-plus]="!isStudentPicked(s.id)"></i>
                    {{ s.fullName }}
                  </button>
                } @empty {
                  <p class="tcal-picker__empty">{{ 'CALENDAR.CREATE.NO_STUDENTS' | translate }}</p>
                }
              </div>
            </div>
          }
        </section>

        <!-- ─── Quand et où ───────────────────────────────────────────── -->
        <section class="tcal-sec tcal-sec--when">
          <h3 class="tcal-sec__title">{{ 'CALENDAR.CREATE.SECTION_WHEN' | translate }}</h3>

          <div class="tcal-field-row">
            <div class="tcal-field" [class.is-invalid]="dateInvalid()">
              <label for="sess-date">{{ 'CALENDAR.CREATE.DATE' | translate }} *</label>
              <input id="sess-date" type="date" [(ngModel)]="form.date"
                     [attr.aria-invalid]="dateInvalid() || null">
              @if (dateInvalid()) {
                <p class="tcal-field__error" role="alert">
                  <i class="fa-solid fa-circle-exclamation"></i>
                  {{ 'CALENDAR.CREATE.DATE_REQUIRED' | translate }}
                </p>
              }
            </div>
            <div class="tcal-field" [class.is-invalid]="startInvalid()">
              <label for="sess-start">{{ 'CALENDAR.CREATE.START_TIME' | translate }} *</label>
              <input id="sess-start" type="time" [(ngModel)]="form.startTime"
                     [attr.aria-invalid]="startInvalid() || null">
              @if (startInvalid()) {
                <p class="tcal-field__error" role="alert">
                  <i class="fa-solid fa-circle-exclamation"></i>
                  {{ 'CALENDAR.CREATE.START_REQUIRED' | translate }}
                </p>
              }
            </div>
            <!-- La fin se calcule, mais se saisit aussi : caler une séance
                 « jusqu'à 10h15 » vaut mieux que compter les minutes. -->
            <div class="tcal-field">
              <label for="sess-end">{{ 'CALENDAR.CREATE.END_TIME' | translate }}</label>
              <input id="sess-end" type="time" [(ngModel)]="endTime">
            </div>
          </div>

          <!-- Durées en segments : une liste déroulante coûtait trois gestes
               pour un choix qui tient sur une ligne. -->
          <fieldset class="tcal-group tcal-durations">
            <legend class="tcal-field__label">
              {{ 'CALENDAR.CREATE.DURATION' | translate }}
              @if (!isPresetDuration) {
                <!-- Durée issue de l'heure de fin : aucun segment n'est actif,
                     il faut donc dire laquelle s'applique. -->
                <span class="tcal-duration__current">
                  {{ form.durationMinutes }} {{ 'CALENDAR.MINUTES' | translate }}
                </span>
              }
            </legend>
            @for (d of durations; track d) {
              <button type="button" class="tcal-duration"
                      [class.is-active]="form.durationMinutes === d"
                      [attr.aria-pressed]="form.durationMinutes === d"
                      (click)="form.durationMinutes = d">
                {{ d }}<span class="tcal-duration__unit">{{ 'CALENDAR.MINUTES' | translate }}</span>
              </button>
            }
          </fieldset>

          <div class="tcal-field-row">
            <div class="tcal-field">
              <label for="sess-location">{{ 'CALENDAR.CREATE.LOCATION' | translate }}</label>
              <input id="sess-location" type="text" [(ngModel)]="form.location"
                     [placeholder]="'CALENDAR.CREATE.LOCATION_PLACEHOLDER' | translate">
            </div>
            <div class="tcal-field">
              <label for="sess-link">{{ 'CALENDAR.CREATE.MEETING_LINK' | translate }}</label>
              <input id="sess-link" type="url" [(ngModel)]="form.meetingUrl"
                     placeholder="https://meet.google.com/…">
            </div>
          </div>

          <div class="tcal-field">
            <label for="sess-notes">{{ 'CALENDAR.CREATE.DESCRIPTION' | translate }}</label>
            <textarea id="sess-notes" rows="2" [(ngModel)]="form.reason"
                      [placeholder]="'CALENDAR.CREATE.DESCRIPTION_PLACEHOLDER' | translate"></textarea>
          </div>
        </section>

        <!-- Les erreurs d'enregistrement passent par une alerte SweetAlert :
             affichées ici, en bas d'une zone qui défile, elles restaient hors
             de vue au moment précis où le professeur venait de cliquer. -->
      </div>

      <footer class="tcal-modal__foot">
        <button type="button" class="tcal-btn-ghost" (click)="closeForm()">
          {{ 'CALENDAR.CREATE.CANCEL' | translate }}
        </button>
        <button type="button" class="tcal-btn-primary"
                [disabled]="saving() || !formValid" (click)="submit()">
          @if (saving()) { <span class="tcal-spinner tcal-spinner--sm"></span> }
          {{ (editingId() === null ? 'CALENDAR.CREATE.SUBMIT' : 'CALENDAR.EDIT.SUBMIT') | translate }}
        </button>
      </footer>
      </div>
    }
  </dialog>

  <!-- ─── Détail d'une séance ──────────────────────────────────────── -->
  <dialog #detailDialog class="tcal-dialog tcal-drawer-host"
          (close)="onDetailClosed()"
          (cancel)="onDetailCancel($event)"
          (mousedown)="onPressStart($event)"
          (click)="onBackdropClick($event, detailDialog)">
    @if (selected(); as s) {
    <aside class="tcal-drawer" [class]="view.colorClass(s)" [attr.aria-label]="s.title">
      <button type="button" class="tcal-drawer__close" (click)="closeDetail()"
              [attr.aria-label]="'CALENDAR.CREATE.CANCEL' | translate">
        <i class="fa-solid fa-xmark"></i>
      </button>

      <span class="tcal-drawer__kind">
        <i [class]="view.kindIcon(s.sessionKind)"></i>
        {{ view.kindLabelKey(s.sessionKind) | translate }}
      </span>

      <h2 class="tcal-drawer__title">{{ s.title }}</h2>

      <!-- Rejoindre ou annuler peut échouer : le message reste dans le tiroir,
           en tête, plutôt qu'au-dessus de lui où il n'était pas cliquable. -->
      @if (detailError()) {
        <p class="tcal-compose__alert" role="alert">
          <i class="fa-solid fa-circle-exclamation"></i>
          <span>{{ detailError() }}</span>
          <button type="button" class="tcal-compose__alert-close"
                  (click)="detailError.set(null)"
                  [attr.aria-label]="'ACTIONS.CLOSE' | translate">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </p>
      }

      @if (s.isProduction) {
        <p class="tcal-note tcal-note--flat">
          <i class="fa-solid fa-clapperboard"></i>
          <span>{{ 'CALENDAR.PRODUCTION.BADGE' | translate }}</span>
        </p>
      }

      <ul class="tcal-drawer__meta">
        <li>
          <i class="fa-solid fa-calendar"></i>
          {{ start(s) | localizedDate:'EEEE dd MMMM yyyy' }}
        </li>
        <li>
          <i class="fa-solid fa-clock"></i>
          {{ time(s.startTime) }} – {{ time(s.endTime) }}
          <span class="tcal-drawer__dim">({{ s.durationMinutes }} {{ 'CALENDAR.MINUTES' | translate }})</span>
        </li>
        <!-- Niveau, toutes les matières, groupe, élèves : chacun sous son libellé.
             N'en montrer qu'une matière décrirait une séance qui n'existe pas. -->
        <li><app-session-audience [session]="s"></app-session-audience></li>
        @if (s.location) {
          <li><i class="fa-solid fa-location-dot"></i> {{ s.location }}</li>
        }
      </ul>

      @if (s.reason) {
        <p class="tcal-drawer__notes">{{ s.reason }}</p>
      }

      @if (s.meetingUrl) {
        <!-- Le prof passe par le même chemin que les élèves : son accès est le
             signal qui atteste que la séance a bien démarré. -->
        <button type="button" class="tcal-drawer__join" (click)="join(s)">
          <i class="fa-solid fa-video"></i>
          {{ 'CALENDAR.START_MEETING' | translate }}
        </button>
      }

      <!-- Bilan de présence, reconstruit depuis le journal d'accès -->
      @if (attendance(); as att) {
        <div class="tcal-attend" [class]="'tcal-attend--' + att.verdict.toLowerCase()">
          <span class="tcal-attend__verdict">
            <i [class]="verdictIcon(att.verdict)"></i>
            {{ ('CALENDAR.VERDICT.' + att.verdict.toUpperCase()) | translate }}
          </span>
          @if (att.isOnline) {
            <span class="tcal-attend__count">
              <i class="fa-solid fa-user-check"></i>
              {{ att.presentStudents }} / {{ att.expectedStudents }}
              {{ 'CALENDAR.PRESENT' | translate }}
            </span>
          }
        </div>
      } @else if (attendanceLoading()) {
        <p class="tcal-drawer__dim">{{ 'LOADINGS.LOADING' | translate }}</p>
      }

      <!-- ─── Issue d'une séance de production ──────────────────────────
           Sans participant, aucun journal d'accès ne peut attester la tenue :
           c'est le professeur qui la déclare. -->
      @if (s.isProduction) {
        <section class="tcal-outcome">
          <!-- En tete de section : en pied, le message se retrouvait sous les
               boutons, hors de vue tant qu'on n'avait pas fait defiler. -->
          @if (outcomeError()) {
            <p class="tcal-alert tcal-alert--error" role="alert">
              <i class="fa-solid fa-circle-exclamation"></i>
              {{ outcomeError() }}
            </p>
          }

          @if (s.held === true) {
            <p class="tcal-outcome__verdict tcal-outcome__verdict--held">
              <i class="fa-solid fa-circle-check"></i>
              {{ 'CALENDAR.PRODUCTION.DONE' | translate }}
            </p>
          } @else if (s.held === false) {
            <p class="tcal-outcome__verdict tcal-outcome__verdict--notheld">
              <i class="fa-solid fa-circle-xmark"></i>
              {{ 'CALENDAR.PRODUCTION.NOT_DONE' | translate }}
            </p>
            @if (s.consultantNotes) {
              <p class="tcal-drawer__notes">{{ s.consultantNotes }}</p>
            }
          } @else if (canDeclare(s)) {
            <p class="tcal-outcome__ask">{{ 'CALENDAR.PRODUCTION.ASK' | translate }}</p>

            @if (declining()) {
              <div class="tcal-field">
                <label for="decline-reason">{{ 'CALENDAR.PRODUCTION.REASON' | translate }} *</label>
                <textarea id="decline-reason" rows="2" [(ngModel)]="declineReason"
                          [placeholder]="'CALENDAR.PRODUCTION.REASON_PLACEHOLDER' | translate"></textarea>
              </div>
              <div class="tcal-outcome__actions">
                <button type="button" class="tcal-btn-ghost" (click)="cancelDecline()">
                  {{ 'CALENDAR.CREATE.CANCEL' | translate }}
                </button>
                <button type="button" class="tcal-btn-danger"
                        [disabled]="outcomeSaving() || !declineReason.trim()"
                        (click)="declare(s, false)">
                  @if (outcomeSaving()) { <span class="tcal-spinner tcal-spinner--sm"></span> }
                  {{ 'CALENDAR.PRODUCTION.CONFIRM_NOT_DONE' | translate }}
                </button>
              </div>
            } @else {
              <div class="tcal-outcome__actions">
                <button type="button" class="tcal-btn-primary"
                        [disabled]="outcomeSaving()" (click)="declare(s, true)">
                  <i class="fa-solid fa-check"></i>
                  {{ 'CALENDAR.PRODUCTION.MARK_DONE' | translate }}
                </button>
                <button type="button" class="tcal-btn-ghost"
                        [disabled]="outcomeSaving()" (click)="declare(s, false)">
                  {{ 'CALENDAR.PRODUCTION.MARK_NOT_DONE' | translate }}
                </button>
              </div>
            }
          } @else {
            <p class="tcal-drawer__dim">{{ 'CALENDAR.PRODUCTION.TOO_EARLY' | translate }}</p>
          }

        </section>
      }

      <!-- ─── Enregistrements rattachés ────────────────────────────────── -->
      <section class="tcal-rec">
        <h3 class="tcal-rec__title">
          <i class="fa-solid fa-film"></i>
          {{ 'CALENDAR.RECORDING.TITLE' | translate }}
        </h3>

        @for (r of s.recordings ?? []; track r.id) {
          <article class="tcal-rec__item" [class.is-archived]="r.isArchived">
            @if (r.previewUrl && r.category === 'Video') {
              <!-- Lecture depuis le stockage, sans repasser par l'API. -->
              <video class="tcal-rec__player" controls preload="metadata" [src]="r.previewUrl"></video>
            }
            <div class="tcal-rec__meta">
              <span class="tcal-rec__name">
                {{ r.title }}
                <!-- Une pièce archivée reste visible, mais dite comme telle :
                     la masquer faisait croire qu'elle avait disparu. -->
                @if (r.isArchived) {
                  <span class="tcal-rec__tag tcal-rec__tag--archived">
                    {{ 'CALENDAR.RECORDING.ARCHIVED' | translate }}
                  </span>
                }
                @if (!r.isPublic && !r.isArchived) {
                  <span class="tcal-rec__tag tcal-rec__tag--private">
                    {{ 'CALENDAR.RECORDING.NOT_PUBLISHED' | translate }}
                  </span>
                }
              </span>
              <span class="tcal-rec__dim">
                {{ fileSize(r.fileSizeBytes) }}
                @if (r.fileSizeBytes) { · }
                {{ r.createdAt | date:'dd/MM/yyyy' }}
              </span>
            </div>
            @if (r.previewUrl && r.category !== 'Video') {
              <a class="tcal-rec__link" [href]="r.previewUrl" target="_blank" rel="noopener">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
                {{ 'CALENDAR.RECORDING.OPEN' | translate }}
              </a>
            }
          </article>
        } @empty {
          <p class="tcal-drawer__dim">{{ 'CALENDAR.RECORDING.EMPTY' | translate }}</p>
        }

        @if (uploadOpen()) {
          <div class="tcal-rec__form">
            <!-- En tete du formulaire, pas en pied : dans un tiroir defilant,
                 le message n'apparaissait qu'apres avoir fait defiler, et le
                 depot semblait ne rien repondre. -->
            @if (uploadError()) {
              <p class="tcal-alert tcal-alert--error" role="alert">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ uploadError() }}
              </p>
            }

            <div class="tcal-field">
              <label for="rec-title">{{ 'CALENDAR.RECORDING.NAME' | translate }} *</label>
              <input id="rec-title" type="text" [(ngModel)]="recordingTitle">
            </div>
            <div class="tcal-field">
              <label for="rec-file">{{ 'CALENDAR.RECORDING.FILE' | translate }} *</label>
              <input id="rec-file" type="file" accept="video/*" (change)="onRecordingPicked($event)">
              <span class="tcal-rec__dim">{{ 'CALENDAR.RECORDING.LIMIT' | translate }}</span>
            </div>

            @if (uploadPercent() !== null) {
              <div class="tcal-progress">
                <progress class="tcal-progress__bar" max="100" [value]="uploadPercent()"
                          [attr.aria-label]="'CALENDAR.RECORDING.SEND' | translate"></progress>
                <span class="tcal-progress__pct">{{ uploadPercent() }}%</span>
              </div>
            }

            <div class="tcal-outcome__actions">
              <button type="button" class="tcal-btn-ghost"
                      [disabled]="uploadBusy()" (click)="closeUpload()">
                {{ 'CALENDAR.CREATE.CANCEL' | translate }}
              </button>
              <button type="button" class="tcal-btn-primary"
                      [disabled]="uploadBusy() || !recordingValid"
                      (click)="uploadRecording(s)">
                @if (uploadBusy()) { <span class="tcal-spinner tcal-spinner--sm"></span> }
                {{ 'CALENDAR.RECORDING.SEND' | translate }}
              </button>
            </div>
          </div>
        } @else {
          <button type="button" class="tcal-btn-ghost tcal-rec__add" (click)="openUpload(s)">
            <i class="fa-solid fa-cloud-arrow-up"></i>
            {{ 'CALENDAR.RECORDING.ADD' | translate }}
          </button>
        }
      </section>

      <div class="tcal-drawer__actions">
        <button type="button" class="tcal-btn-ghost" (click)="openEdit(s)">
          <i class="fa-solid fa-pen"></i>
          {{ 'CALENDAR.EDIT.ACTION' | translate }}
        </button>
        <button type="button" class="tcal-btn-danger" (click)="cancelSession(s)">
          <i class="fa-solid fa-ban"></i>
          {{ 'CALENDAR.CANCEL_ACTION' | translate }}
        </button>
      </div>
    </aside>
    }
  </dialog>

</div>
